import React, { useEffect, useRef, useState } from 'react';
import PropTypes from "prop-types";
import withRouter from '../Components/Common/withRouter';

//import Components
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import RightSidebar from '../Components/Common/RightSidebar';
import GlobalJobProgress from './GlobalJobProgress';

//import actions
import {
    changeLayout,
    changeSidebarTheme,
    changeLayoutMode,
    changeLayoutWidth,
    changeLayoutPosition,
    changeTopbarTheme,
    changeLeftsidebarSizeType,
    changeLeftsidebarViewType,
    changeSidebarImageType,
    changeSidebarVisibility
} from "../slices/thunks";

//redux
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from 'reselect';

//constants
import { LAYOUT_SIDEBAR_TYPES } from "../Components/constants/layout";

//brand theme
import {
    applyPrimaryBrandColor,
    clearPrimaryBrandColor,
    applyCustomSidebarBackground,
    clearCustomSidebarBackground,
} from "../utils/colorTheme";
import { syncBrandingConfig } from "../utils/brandingSync";

const Layout = (props : any) => {
    const [headerClass, setHeaderClass] = useState("");
    const dispatch : any = useDispatch();

    // Màu thương hiệu (dùng chung API với trang Brand Management)
    const selectBrandingState = (state: any) => state.BrandingConfiguration;
    const selectBrandingColor = createSelector(
        selectBrandingState,
        (branding) => branding.config?.primaryColor as string | undefined
    );
    const brandPrimaryColor = useSelector(selectBrandingColor);
    const hasAppliedDefaultBrandSidebar = useRef(false);

    // Nguồn dữ liệu gốc của thương hiệu (logo, tên, màu...) - mọi thay đổi từ
    // trang Brand Management hay bộ chọn màu ở RightSidebar đều đi qua đây.
    // Tự động đồng bộ sang BrandingConfiguration (state Header đang đọc để
    // hiển thị logo/tên) + cache, để topbar/sidebar cập nhật ngay lập tức và
    // vẫn đúng sau khi F5 (không phải chờ cache 5 phút hết hạn).
    const brandManagementInfo = useSelector((state: any) => state.BrandManagement.brandInfo);
    useEffect(() => {
        if (brandManagementInfo) {
            syncBrandingConfig(dispatch, brandManagementInfo);
        }
    }, [brandManagementInfo, dispatch]);

    const selectLayoutState = (state : any) => state.Layout;
    const selectLayoutProperties = createSelector(
        selectLayoutState,
        (layout) => ({
            layoutType: layout.layoutType,
            leftSidebarType: layout.leftSidebarType,
            layoutModeType: layout.layoutModeType,
            layoutWidthType: layout.layoutWidthType,
            layoutPositionType: layout.layoutPositionType,
            topbarThemeType: layout.topbarThemeType,
            leftsidbarSizeType: layout.leftsidbarSizeType,
            leftSidebarViewType: layout.leftSidebarViewType,
            leftSidebarImageType: layout.leftSidebarImageType,
            preloader: layout.preloader,
            sidebarVisibilitytype: layout.sidebarVisibilitytype,
        })
    );
    // Inside your component
    const {
        layoutType,
        leftSidebarType,
        layoutModeType,
        layoutWidthType,
        layoutPositionType,
        topbarThemeType,
        leftsidbarSizeType,
        leftSidebarViewType,
        leftSidebarImageType,
        sidebarVisibilitytype
    } = useSelector(selectLayoutProperties);

    /*
    layout settings
    */
    useEffect(() => {
        if (
            layoutType ||
            leftSidebarType ||
            layoutModeType ||
            layoutWidthType ||
            layoutPositionType ||
            topbarThemeType ||
            leftsidbarSizeType ||
            leftSidebarViewType ||
            leftSidebarImageType ||
            sidebarVisibilitytype
        ) {
            window.dispatchEvent(new Event('resize'));
            dispatch(changeLeftsidebarViewType(leftSidebarViewType));
            dispatch(changeLeftsidebarSizeType(leftsidbarSizeType));
            dispatch(changeSidebarTheme(leftSidebarType));
            dispatch(changeLayoutMode(layoutModeType));
            dispatch(changeLayoutWidth(layoutWidthType));
            dispatch(changeLayoutPosition(layoutPositionType));
            dispatch(changeTopbarTheme(topbarThemeType));
            dispatch(changeLayout(layoutType));
            dispatch(changeSidebarImageType(leftSidebarImageType));
            dispatch(changeSidebarVisibility(sidebarVisibilitytype));
        }
    }, [layoutType,
        leftSidebarType,
        layoutModeType,
        layoutWidthType,
        layoutPositionType,
        topbarThemeType,
        leftsidbarSizeType,
        leftSidebarViewType,
        leftSidebarImageType,
        sidebarVisibilitytype,
        dispatch]);

    /*
    Layout settings không được lưu lại (reload là mất, quay về mặc định "dark").
    Vì primaryColor thì có lưu qua API, nên nếu công ty đã cấu hình màu thương
    hiệu, mặc định sidebar sẽ hiển thị "Thương hiệu" thay vì Dark mỗi khi tải
    lại trang. Chỉ áp dụng 1 lần khi vừa có màu và sidebar còn đang ở mặc định
    "dark" (chưa bị người dùng đổi trong phiên hiện tại).
    */
    useEffect(() => {
        if (
            !hasAppliedDefaultBrandSidebar.current &&
            brandPrimaryColor &&
            leftSidebarType === LAYOUT_SIDEBAR_TYPES.DARK
        ) {
            hasAppliedDefaultBrandSidebar.current = true;
            dispatch(changeSidebarTheme(LAYOUT_SIDEBAR_TYPES.CUSTOM));
        }
    }, [brandPrimaryColor, leftSidebarType, dispatch]);

    /*
    Áp dụng màu thương hiệu (primaryColor) thực sự lên toàn app: button, badge,
    link, border... (đồng bộ với màu chọn ở trang Brand Management), và áp nền
    sidebar theo màu đó khi đang ở chế độ "custom".
    */
    useEffect(() => {
        if (brandPrimaryColor) {
            applyPrimaryBrandColor(brandPrimaryColor);
        } else {
            clearPrimaryBrandColor();
        }
    }, [brandPrimaryColor]);

    useEffect(() => {
        if (leftSidebarType === LAYOUT_SIDEBAR_TYPES.CUSTOM && brandPrimaryColor) {
            applyCustomSidebarBackground(brandPrimaryColor);
        } else {
            clearCustomSidebarBackground();
        }
    }, [leftSidebarType, brandPrimaryColor]);

    /*
    call dark/light mode
    */
    const onChangeLayoutMode = (value : any) => {
        if (changeLayoutMode) {
            dispatch(changeLayoutMode(value));
        }
    };

    // class add remove in header 
    useEffect(() => {
        window.addEventListener("scroll", scrollNavigation, true);
        return () => {
            window.removeEventListener("scroll", scrollNavigation, true);
        };
    }, []);

    function scrollNavigation() {
        var scrollup = document.documentElement.scrollTop;
        if (scrollup > 50) {
            setHeaderClass("topbar-shadow");
        } else {
            setHeaderClass("");
        }
    }

    useEffect(() => {
        const humberIcon = document.querySelector(".hamburger-icon") as HTMLElement;
        if (sidebarVisibilitytype === 'show' || layoutType === "vertical" || layoutType === "twocolumn") {
            humberIcon.classList.remove('open');
        } else {
            humberIcon && humberIcon.classList.add('open');
        }
    }, [sidebarVisibilitytype, layoutType]);

    return (
        <React.Fragment>
            <div id="layout-wrapper">
                <Header
                    headerClass={headerClass}
                    layoutModeType={layoutModeType}
                    onChangeLayoutMode={onChangeLayoutMode} />
                <Sidebar
                    layoutType={layoutType}
                />
                <div className="main-content">
                    {props.children}
                    <Footer />
                </div>
            </div>
            <GlobalJobProgress />
            <RightSidebar />
        </React.Fragment>

    );
};

Layout.propTypes = {
    children: PropTypes.object,
};

export default withRouter(Layout);
